import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { EASE } from "./Reveal";

export function Lightbox({ open, images, index, title, onClose, onChange }) {
  const count = images.length;
  const prev = useCallback(() => onChange((index - 1 + count) % count), [index, count, onChange]);
  const next = useCallback(() => onChange((index + 1) % count), [index, count, onChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={`${title} gallery`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050505]/95 backdrop-blur-xl"
          onClick={onClose}
          data-testid="lightbox"
        >
          <img
            src={images[index]}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-20 blur-3xl"
          />

          <div className="absolute left-6 top-6 flex items-center gap-4 font-jbmono text-xs tracking-[0.25em] text-zinc-400 md:left-10 md:top-8">
            <span className="text-[#00E5FF]">{title}</span>
            <span data-testid="lightbox-counter">
              {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close gallery"
            className="absolute right-6 top-6 rounded-full border border-white/15 bg-black/40 p-3 text-white transition-[background-color,color,transform] duration-300 hover:scale-105 hover:bg-[#00E5FF] hover:text-[#050505] md:right-10 md:top-8"
            data-testid="lightbox-close"
          >
            <X size={18} />
          </button>

          <div className="pointer-events-none relative flex h-full w-full items-center justify-center px-6 py-24 md:px-24">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={images[index]}
                src={images[index]}
                alt={`${title} ${index + 1}`}
                onClick={(e) => e.stopPropagation()}
                drag={count > 1 ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.6}
                dragMomentum={false}
                onDragEnd={(_, info) => {
                  const swipe = info.offset.x + info.velocity.x * 0.2;
                  if (swipe < -60) next();
                  else if (swipe > 60) prev();
                }}
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: -8 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="pointer-events-auto max-h-full max-w-full touch-pan-y select-none rounded-lg object-contain shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
                draggable={false}
                data-testid="lightbox-image"
              />
            </AnimatePresence>
          </div>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous image"
                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/40 p-3 text-white transition-[background-color,color,transform] duration-300 hover:scale-105 hover:bg-[#00E5FF] hover:text-[#050505] md:left-8"
                data-testid="lightbox-prev"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next image"
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/40 p-3 text-white transition-[background-color,color,transform] duration-300 hover:scale-105 hover:bg-[#00E5FF] hover:text-[#050505] md:right-8"
                data-testid="lightbox-next"
              >
                <ArrowRight size={18} />
              </button>
            </>
          )}

          {count > 1 && (
            <div
              className="pointer-events-none absolute bottom-20 left-1/2 -translate-x-1/2 font-jbmono text-[10px] tracking-[0.3em] text-zinc-500 md:hidden"
              data-testid="lightbox-swipe-hint"
            >
              SWIPE TO BROWSE
            </div>
          )}

          {count > 1 && (
            <div
              className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2 rounded-full border border-white/15 bg-black/50 p-1.5 backdrop-blur-xl md:bottom-8"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((im, i) => (
                <button
                  key={im}
                  type="button"
                  aria-label={`Go to image ${i + 1}`}
                  onClick={() => onChange(i)}
                  className={`h-10 w-10 overflow-hidden rounded-full border transition-[border-color,opacity,transform] duration-300 hover:scale-110 ${
                    i === index ? "border-[#00E5FF] opacity-100" : "border-white/20 opacity-50"
                  }`}
                  data-testid={`lightbox-thumb-${i}`}
                >
                  <img src={im} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
